
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { ErgState, Erg } from 'shared/state'
import { bus } from 'shared/state/Bus'
import { GlobalConfig, RaceConfig, ServicesConfig } from 'display/config'
import { Voice } from 'shared/types/voice'
import type { RaceDefinition } from 'shared/types/erg/RaceDefinition'
import { config } from 'display/config'
import { sortByKeyAsc } from 'shared/util'
import { Machine, RaceDataItem } from 'shared/types/erg'
import { formatTime } from 'shared/util'

@Component({})
export default class Speech extends Vue {
  @ErgState() definition
  @ErgState() isPaceRace!: boolean
  @ErgState() status
  @ErgState() classes: string[]
  @ErgState() totalElapsed: number
  @GlobalConfig() playAudio!: boolean
  @GlobalConfig() playEliminationSounds!: boolean
  @GlobalConfig() playPaceBoatSounds!: boolean
  @GlobalConfig() suppressAnnouncementsSeconds!: number
  @GlobalConfig() lang: string
  @ServicesConfig() awsRegion!: string
  @ServicesConfig() awsIdentityPoolId!: string
  @ServicesConfig() eliminationTemplate!: string
  @ServicesConfig() paceToBeatTemplate!: string
  @ServicesConfig() voice: string
  @ServicesConfig() sampleRate: string
  @ServicesConfig() engine!: string
  @RaceConfig('pace') paceDisplayType!: string

  announcements = []
  boatVoices: Voice[] = []
  setUp = false
  testAudio = new Audio()

  speechParams = {
    OutputFormat: 'mp3',
    SampleRate: '16000',
    Text: '',
    TextType: 'ssml',
    VoiceId: 'Matthew',
    Engine: 'standard',
  }

  voices = []
  config = config
  meta = []
  playing = false

  // NOT in Vue reactive data — assigning the AWS SDK object or a Polly service instance
  // to reactive data causes Vue 2's observe() to call Object.keys() on objects with
  // hundreds of properties, throwing RangeError in Chrome. No initializer = not in data().
  private polly: any

  get time() {
    return formatTime(
      Math.round(
        Erg[
          window.location.href.indexOf('leaderboard') > -1
            ? 'time'
            : 'bufferedTime'
        ]
      )
    )
  }

  async shiftAndPlay() {
    if (this.announcements.length && !this.playing) {
      this.playing = true
      const announcement = this.announcements.shift()
      announcement.audio.onended = () => {
        this.playing = false
        this.shiftAndPlay()
      }
      this.playSpeech(announcement)
    }
  }
  playSpeech(announcement) {
    if (announcement && this.playAudio) {
      announcement.audio.play().catch((e) => {
        this.playing = false
        if (e.name === 'NotAllowedError') {
          this.announcements = []
        }
      })
      return
    }
    this.playing = false
  }

  async convertText(speechParams): Promise<string> {
    const AWS = (window as any).AWS
    if (!speechParams || !AWS || !AWS.Polly) {
      return
    }
    const polly = new AWS.Polly({ apiVersion: '2016-06-10' })
    const signer = new AWS.Polly.Presigner(speechParams, polly)
    // delete(speechParams.SampleRate)

    /**
     * currently account appears to be blocking certain requests
     * when engine is selected. There may be additional permissions needed on AWS
     * or it may be a limitation of v2 of api.
     * Workaround is to ignore the Engine parameter.
     */

    delete speechParams.Engine
    // console.log('speech params:', JSON.stringify(speechParams))
    return await new Promise(async (resolve) => {
      signer.getSynthesizeSpeechUrl(speechParams, (error, url) => {
        if (error) {
          bus.$emit('speech', 'no_supported_voice')
          return resolve('')
        } else {
          return resolve(url)
        }
      })
    })
  }

  @Watch('engine', { immediate: true, deep: true })
  async engineChanged(newVal) {
    try {
      await this.describeVoices()
    } catch (e) {
      console.error('describeVoices failed:', e)
    }
  }

  @Watch('definition', { immediate: true, deep: true })
  definitionChanged(newVal: RaceDefinition) {
    if (!newVal || (newVal!.race_type !== 'elimination' && !this.isPaceRace)) {
      return
    }
    if (!this.setUp) {
      this.configure()
    }
    const boats = newVal.boats
      .slice()
      .map((b) => ({ lane_number: b.lane_number, name: b.name }))
    const boatVoices = []
    const promises = []
    boats.forEach(async (b) => {
      const speechParams = { ...this.speechParams }
      speechParams.Text = this.eliminationTemplate
        .replace('NAME', b.name)
        .replace(/[_\-]/, ' ')
      speechParams.VoiceId = this.voice
      speechParams.SampleRate = this.sampleRate
      speechParams.Engine = this.engine
      promises.push(
        new Promise(async (resolve) => {
          const url = await this.convertText(speechParams)
          if (url) {
            const v: Voice = {
              lane_number: b.lane_number,
              name: b.name,
              audio: new Audio(url),
            }
            v.audio.load()
            boatVoices.push(v)
          }
          resolve(null)
        })
      )
    })
    Promise.all(promises).then(() => {
      this.boatVoices = boatVoices
    })
    // console.log('promises:', promises)
    // console.log('boatVoices:', this.boatVoices)
  }
  pace(d: RaceDataItem): string {
    switch (this.paceDisplayType) {
      case 'pace':
        return Erg.unit === 'calories'
          ? d.calories_per_hour.toString() || ''
          : d.pace
          ? formatTime(Math.floor(d.pace))
              .replace('1:00', '1 minute')
              .replace(':00', 'minutes')
          : ''
      case 'avg_pace':
        return Erg.unit === 'calories'
          ? d.avg_calories_per_hour.toString() || ''
          : d.avg_pace
          ? formatTime(d.avg_pace, 1)
              .replace('1:00', '1 minute')
              .replace(':00', 'minutes')
          : ''
      case 'avg_watts':
        return d.avg_watts ? d.avg_watts.toFixed(0) : ''
      case 'watts':
        return d.watts ? d.watts.toFixed(0) : ''
      default:
        return ''
    }
  }

  playLaneEliminated(lane) {
    if (!this.playEliminationSounds) {
      return
    }
    const announcement = this.boatVoices.find((v) => v.lane_number === lane)
    if (announcement) {
      this.announcements.push(announcement)
      this.shiftAndPlay()
    }
  }
  async playPaceboatPace(d: RaceDataItem) {
    if (
      !this.playPaceBoatSounds ||
      this.totalElapsed < this.suppressAnnouncementsSeconds
    ) {
      return
    }
    let text = this.paceToBeatTemplate
    if (this.classes.length > 1) {
      const c = this.definition.boats.find(
        (b) => b.lane_number === d.lane
      ).class_name
      text = text.replace('<speak>', `<speak>${c} Class `)
    }
    const speechParams = { ...this.speechParams }
    text = text.replace('M:SS', this.pace(d))
    speechParams.Text = text
    const url = await this.convertText(speechParams)
    if (!url) {
      return
    }
    const announcement: Voice = {
      lane_number: d.lane,
      name: '',
      audio: new Audio(url),
    }
    this.announcements.push(announcement)
    this.shiftAndPlay()
  }
  configure() {
    try {
      const AWS = (window as any).AWS
      if (!AWS || !AWS.config || !AWS.Polly) {
        return
      }
      if (!this.awsRegion || !this.awsIdentityPoolId) {
        return
      }
      AWS.config.logger = console
      AWS.config.region = this.awsRegion
      AWS.config.credentials = new AWS.CognitoIdentityCredentials({
        IdentityPoolId: this.awsIdentityPoolId,
      })
      this.polly = new AWS.Polly({
        apiVersion: '2016-06-10',
        region: this.awsRegion,
      })
      this.setUp = true
    } catch (e) {
      console.error('AWS configure failed:', e)
    }
  }

  async testSpeech(template: string) {
    if (!this.setUp) {
      this.configure()
    }
    if (this.voices.length < 2) {
      await this.describeVoices()
    }
    this.speechParams.VoiceId = this.voice
    this.speechParams.SampleRate = this.sampleRate
    this.speechParams.Engine = this.engine
    this.speechParams.Text = template.includes('Pace')
      ? this.paceToBeatTemplate.replace('M:SS', '3:22')
      : this.eliminationTemplate.replace('NAME', 'LANE 1').replace(/[_\-]/, ' ')
    const url = await this.convertText(this.speechParams)
    if (!url) { return }
    this.testAudio = new Audio(url)
    this.testAudio.load()
    try {
      await this.testAudio.play()
    } catch { /* some browsers reject the play() promise; others throw synchronously */ }
  }

  async describeVoices() {
    bus.$emit('speech', 'retrieving_voices')
    if (!this.setUp) {
      this.configure()
    }
    if (!this.setUp) {
      return
    }
    const params = {
      LanguageCode: this.lang,
    }
    try {
      // @ts-ignore — AWS SDK v2 Request has .promise(); stub type doesn't
      const data = await (this.polly.describeVoices(params) as any).promise()
      let meta = this.meta
      meta.forEach((m, i) => {
        const a: string[] = Object.keys(m)
        a.forEach((prop) => {
          meta[i][prop.toLowerCase().replace('_', '-')] = meta[i][prop]
        })
      })
      meta = meta.filter((mm) => mm.code === this.lang && mm[this.engine])
      const v = data.Voices.map((d) => ({ value: d.Id, text: d.Name })).sort(
        (a, b) => sortByKeyAsc(a, b, 'value')
      )
      this.voices = v.filter((v) => {
        return meta.find((mm) => mm.id === v.value)
      })
      bus.$emit('update_options', 'Voice', this.voices)
    } catch (e) {
      console.log(e)
    } finally {
      bus.$emit('speech', 'retrieving_voices_completed')
    }
  }
  setVal(controlLabel, value) {
    if (controlLabel === 'Voice') {
      config.set({ servicesConfig: { voice: value } })
      this.speechParams.VoiceId = value
    }
  }
  async selectControlClicked(controlLabel: string) {
    if (controlLabel === 'Voice') {
      await this.describeVoices()
      this.definitionChanged({ ...this.definition })
    }
  }

  async created() {
    try {
      try {
        const res = await fetch('/assets/voices.json')
        if (!res.ok) {
          console.error('voices.json failed to load', res.status)
          this.meta = []
        } else {
          this.meta = await res.json()
        }
      } catch (e) {
        console.error('Network error loading voices.json', e)
        this.meta = []
      }

      Erg.$on('lane_eliminated', this.playLaneEliminated)
      Erg.$on('paceboat_pace', this.playPaceboatPace)
      bus.$on('test_speech', this.testSpeech)
      bus.$on('input', this.setVal)
      bus.$on('click', this.selectControlClicked)
      // Wait for AWS to load
      if (!(window as any).AWS) {
        await new Promise((r) => setTimeout(r, 1000))
      }
      try {
        this.configure()
      } catch (e) {
        console.error('AWS Polly setup failed:', e)
      }
    } catch (e) {
      console.error('Speech component init failed:', e)
    }
  }

  beforeDestroy() {
    Erg.$off('lane_eliminated', this.playLaneEliminated)
    Erg.$off('paceboat_pace', this.playPaceboatPace)
    bus.$off('test_speech', this.testSpeech)
    bus.$off('input', this.setVal)
    bus.$off('click', this.selectControlClicked)
  }
}
